/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect } from 'react';

import { Layout } from '@/components/Layout';
import { PaidModal } from '@/components/PaidModal';
import {
	acceptInviteUrl,
	copilotUrl,
	dashboardUrl,
	directMessagesUrl,
	directMessageUrl,
	inviteLinkUrl,
	libraryUrl,
	loginUrl,
	onboardingUrl,
	reelsUrl,
	reportsUrl,
	setlistsUrl,
	songsUrl,
	videoUrl,
} from '@/config/urls';
import { useFireAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { AuthProvider, isOnboarding, useAuth } from '@/providers/AuthProvider';
import IntercomProvider from '@/providers/IntercomProvider/IntercomProvider';
// eslint-disable-next-line import/order
import { IonApp } from '@ionic/react';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/typography.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { Navigate, Route, Routes } from 'react-router';
import useVH from 'react-vh';

import './App.scss';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AlbumGradientPreview } from './components/shared/AlbumGradientPreview';
import { EnqueueSnackbar } from './components/shared/EnqueueSnackbar';
import { VersionChecker } from './components/VersionChecker';
import { useStatusBar } from './hooks/useStatusBar';
import AcceptInvite from './pages/AcceptInvite/AcceptInvite';
import { ChatPage } from './pages/Chat';
import CopilotPage from './pages/Copilot/CopilotPage';
import { Dashboard } from './pages/Dashboard/Dashboard';
import { DirectMessages } from './pages/DirectMessages';
import InviteLink from './pages/InviteLink/InviteLink';
import { Library } from './pages/Library';
import LoginPage from './pages/Login/LoginPage';
import Onboarding from './pages/Onboarding';
import { Reels } from './pages/Reels';
import { Reports } from './pages/Reports';
import { Setlists } from './pages/Setlists';
import { Songs } from './pages/Songs';
import { VideoPlayer } from './pages/VideoPlayer';
import { ActiveStatusProvider } from './providers/ActiveStatusProvider';
import { AnalyticsProvider } from './providers/AnalyticsProvider';
import AppUrlListener from './providers/AppUrlListener/AppUrlListener';
import { LayoutProvider } from './providers/LayoutProvider';
import { LoadingProvider } from './providers/LoadingProvider';
import { OneSignalProvider } from './providers/OnesignalProvider';
import { PermissionsProvider } from './providers/PermissionsProvider';
import { ServicesProvider } from './providers/ServicesProvider';
import { SetlistsProvider } from './providers/SetlistsProvider';
import { SubscriptionProvider } from './providers/SubscriptionProvider';
import { TeamActionSubscriptionProvider } from './providers/TeamActionSubscription';
import { TeamProvider } from './providers/TeamProvider';
import { TeamSongsProvider } from './providers/TeamSongsProvider';
import { UnreadChatsProvider } from './providers/UnreadChatsProvider';
import constants from './theme/exportedVariables.module.scss';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// networkMode: 'always',
			// refetchOnWindowFocus: false,
			// staleTime: Infinity,
		},
		mutations: {
			// networkMode: 'always',
		},
	},
});

const AuthenticatedApp = () => {
	const { currentMode, setStatusBarColor } = useStatusBar();
	const { user } = useAuth();
	const isNudgeClick = new URLSearchParams(window.location.search).get('utm_source') === 'nudge_setlist';

	useFireAnalyticsEvent({ eventName: 'Nudge_Setlist_Click', enabled: isNudgeClick });

	useEffect(() => {
		setStatusBarColor(constants.colorLight0, constants.colorDark0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentMode]);

	return (
		<ErrorBoundary>
			<LoadingProvider>
				<PermissionsProvider>
					<SubscriptionProvider>
						<TeamSongsProvider>
							<SetlistsProvider>
								<TeamProvider>
									<AnalyticsProvider>
										<IntercomProvider>
											<UnreadChatsProvider>
												<ActiveStatusProvider>
													<ServicesProvider>
														<TeamActionSubscriptionProvider>
															<LayoutProvider>
																<Helmet>
																	<meta charSet="utf-8" />
																	<title>
																		Asaph
																	</title>
																</Helmet>
																<Routes>
																	<Route
																		path={
																			reelsUrl
																		}
																		element={
																			<Layout>
																				<Reels />
																			</Layout>
																		}
																	/>
																	<Route
																		path={
																			videoUrl
																		}
																		element={
																			<VideoPlayer />
																		}
																	/>
																	<Route
																		path={
																			dashboardUrl
																		}
																		element={
																			<Layout>
																				<Dashboard />
																			</Layout>
																		}
																	/>
																	<Route
																		path={
																			libraryUrl
																		}
																		element={
																			<Layout>
																				<Library />
																			</Layout>
																		}
																	/>
																	<Route
																		path={
																			directMessagesUrl
																		}
																		element={
																			<Layout>
																				<DirectMessages />
																			</Layout>
																		}
																	/>
																	<Route
																		path={
																			directMessageUrl
																		}
																		element={
																			<Layout>
																				<ChatPage />
																			</Layout>
																		}
																	/>
																	<Route
																		path={
																			songsUrl
																		}
																		element={
																			<Layout>
																				<Songs />
																			</Layout>
																		}
																	/>
																	<Route
																		path={
																			setlistsUrl
																		}
																		element={
																			<Layout>
																				<Setlists />
																			</Layout>
																		}
																	/>
																	<Route
																		path={
																			reportsUrl
																		}
																		element={
																			<Layout>
																				<Reports />
																			</Layout>
																		}
																	/>

																	{isOnboarding(
																		user,
																	) && (
																		<Route
																			path={
																				onboardingUrl
																			}
																			element={
																				<Onboarding />
																			}
																		/>
																	)}

																	<Route
																		path={
																			inviteLinkUrl
																		}
																		element={
																			<EnqueueSnackbar message="You can't accept an invite link while logged in. Please log out and click the link again.">
																				<Navigate
																					to={
																						dashboardUrl
																					}
																				/>
																			</EnqueueSnackbar>
																		}
																	></Route>

																	<Route
																		path={
																			copilotUrl
																		}
																		element={
																			<Layout>
																				<Songs />
																			</Layout>
																		}
																	/>

																	<Route
																		path="*"
																		element={
																			<Navigate
																				to={
																					dashboardUrl
																				}
																			/>
																		}
																	/>
																</Routes>
															</LayoutProvider>
														</TeamActionSubscriptionProvider>
													</ServicesProvider>
												</ActiveStatusProvider>
											</UnreadChatsProvider>
											<PaidModal />
										</IntercomProvider>
									</AnalyticsProvider>
								</TeamProvider>
							</SetlistsProvider>
						</TeamSongsProvider>
					</SubscriptionProvider>
				</PermissionsProvider>
			</LoadingProvider>
		</ErrorBoundary>
	);
};

const UnAuthenticatedApp: React.FC = () => {
	const { currentMode, setStatusBarColor } = useStatusBar();
	useEffect(() => {
		setStatusBarColor(constants.colorAsaphWhite, constants.colorDark1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentMode]);

	return (
		<AnalyticsProvider>
			<Helmet>
				<meta charSet="utf-8" />
				<title>Asaph</title>
			</Helmet>
			<Routes>
				<Route path={copilotUrl} element={<CopilotPage />} />
				<Route path={loginUrl} element={<LoginPage />} />
				<Route path={acceptInviteUrl} element={<AcceptInvite />} />
				<Route path={inviteLinkUrl} element={<InviteLink />} />
				<Route path="*" element={<Navigate to={loginUrl} />} />
			</Routes>
		</AnalyticsProvider>
	);
};

const App: React.FC = () => {
	useVH();

	return (
		<QueryClientProvider client={queryClient}>
			<IonApp>
				<AppUrlListener />
				<VersionChecker>
					<AuthProvider>
						{({ isAuthenticated }) => (
							<OneSignalProvider>
								{isAuthenticated ? (
									<AuthenticatedApp />
								) : (
									<UnAuthenticatedApp />
								)}
							</OneSignalProvider>
						)}
					</AuthProvider>
				</VersionChecker>
			</IonApp>
			{/* <ReactQueryDevtools /> */}
		</QueryClientProvider>
	);
};

export default App;
